// client/src/coms/views/ComsApp.js
import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ComsLayout from '../components/ComsLayout';
import CommsHomeView from './CommsHomeView';
import { PersonalisationProvider, paintCachedTheme } from '../personalisation/PersonalisationProvider';
import useAccountSessionGuard from '../accounts/useAccountSessionGuard';
import EncryptionConsentHost from '../e2ee/EncryptionConsentHost';
import DeviceVerifyHost from '../e2ee/DeviceVerifyHost';
import '../coms.css';
import '../personalisation/personalisation.css';

function getInviteFromSearch(search) {
  try {
    const s = String(search || '');
    if (!s) return '';
    const sp = new URLSearchParams(s.startsWith('?') ? s : `?${s}`);
    const v = sp.get('invite');
    return String(v || '').trim();
  } catch {
    return '';
  }
}

function readPendingInvite() {
  try {
    if (typeof window === 'undefined') return '';
    const v = window.sessionStorage.getItem('vox_pending_invite');
    return String(v || '').trim();
  } catch {
    return '';
  }
}

function writePendingInvite(code) {
  try {
    if (typeof window === 'undefined') return;
    const v = String(code || '').trim();
    if (!v) return;
    window.sessionStorage.setItem('vox_pending_invite', v);
  } catch {}
}

function clearPendingInvite() {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('vox_pending_invite');
  } catch {}
}

function pickServerIdFromPayload(p) {
  const obj = p && typeof p === 'object' ? p : {};

  const direct =
    obj.server_key != null
      ? String(obj.server_key)
      : obj.public_id != null
        ? String(obj.public_id)
        : obj.server_id != null
          ? String(obj.server_id)
          : obj.serverId != null
            ? String(obj.serverId)
            : obj.id != null
              ? String(obj.id)
              : obj.server && obj.server.server_key != null
                ? String(obj.server.server_key)
                : obj.server && obj.server.public_id != null
                  ? String(obj.server.public_id)
                  : obj.server && obj.server.id != null
                    ? String(obj.server.id)
                    : obj.server && obj.server.server_id != null
                      ? String(obj.server.server_id)
                      : '';

  const out = String(direct || '').trim();
  if (out) return out;

  const list = Array.isArray(obj.servers) ? obj.servers : [];
  if (list.length) {
    const first = list[0] || {};
    const id =
      first.server_key != null
        ? String(first.server_key)
        : first.public_id != null
          ? String(first.public_id)
          : first.id != null
            ? String(first.id)
            : first.server_id != null
              ? String(first.server_id)
              : first.serverId != null
                ? String(first.serverId)
                : '';
    const out2 = String(id || '').trim();
    return out2 ? out2 : null;
  }

  return null;
}

function isStandaloneMode() {
  try {
    if (typeof window === 'undefined') return false;
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    const browserStandalone = !!(mq && mq.matches);
    const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
    const twaReferrer = typeof document !== 'undefined' && String(document.referrer || '').startsWith('android-app://');
    return browserStandalone || iosStandalone || twaReferrer;
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function isTauriDesktop() {
  try {
    return !!(window.__TAURI__ && window.__TAURI__.core && typeof window.__TAURI__.core.invoke === 'function');
  } catch {
    return false;
  }
}

function getTauriCore() {
  try {
    return window.__TAURI__ && window.__TAURI__.core ? window.__TAURI__.core : null;
  } catch {
    return null;
  }
}

function readSkippedUpdateVersion() {
  try {
    if (typeof window === 'undefined') return '';
    return String(window.sessionStorage.getItem('vox_skipped_desktop_update_version') || '').trim();
  } catch {
    return '';
  }
}

function writeSkippedUpdateVersion(version) {
  try {
    if (typeof window === 'undefined') return;
    const v = String(version || '').trim();
    if (!v) return;
    window.sessionStorage.setItem('vox_skipped_desktop_update_version', v);
  } catch {}
}

function clearSkippedUpdateVersion() {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('vox_skipped_desktop_update_version');
  } catch {}
}

function createTauriUpdateResource(metadata) {
  const core = getTauriCore();
  if (!core || !metadata || typeof metadata !== 'object') return null;
  const ResourceCtor = core.Resource;
  if (typeof ResourceCtor !== 'function') return null;

  const rid = Number(metadata.rid);
  if (!Number.isFinite(rid) || rid <= 0) return null;

  const resource = new ResourceCtor(rid);
  resource.currentVersion = String(metadata.currentVersion || '').trim();
  resource.version = String(metadata.version || '').trim();
  resource.date = metadata.date ? String(metadata.date) : '';
  resource.body = metadata.body ? String(metadata.body) : '';
  resource.rawJson = metadata.rawJson && typeof metadata.rawJson === 'object' ? metadata.rawJson : {};
  resource.downloadedBytes = null;

  resource.downloadAndInstall = async (onEvent, options) => {
    const nextOptions = options && typeof options === 'object' ? { ...options } : {};

    if (nextOptions.headers) {
      nextOptions.headers = Array.from(new Headers(nextOptions.headers).entries());
    }

    let channel = null;
    if (typeof core.Channel === 'function') {
      channel = new core.Channel();
      if (typeof onEvent === 'function') {
        channel.onmessage = onEvent;
      }
    }

    await core.invoke('plugin:updater|download_and_install', {
      rid,
      ...(channel ? { onEvent: channel } : {}),
      ...nextOptions,
    });
  };

  resource.close = async () => {
    try {
      if (typeof resource.downloadedBytes?.close === 'function') {
        await resource.downloadedBytes.close();
      }
    } catch {}
    if (typeof ResourceCtor.prototype.close === 'function') {
      await ResourceCtor.prototype.close.call(resource);
    }
  };

  return resource;
}

async function checkForDesktopUpdate() {
  const core = getTauriCore();
  if (!core || typeof core.invoke !== 'function') return null;

  const metadata = await core.invoke('plugin:updater|check', {});
  if (!metadata) return null;

  return createTauriUpdateResource(metadata);
}

async function relaunchDesktopApp() {
  const core = getTauriCore();
  if (!core || typeof core.invoke !== 'function') return;
  await core.invoke('plugin:process|restart');
}

function hasPromptedForPushOnce() {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('vox_push_prompted_v1') === '1';
  } catch {
    return false;
  }
}

function markPromptedForPushOnce() {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('vox_push_prompted_v1', '1');
  } catch {}
}

async function ensureVoxPushSubscription() {
  if (typeof window === 'undefined') return;
  // Settings → Notifications → "Desktop notifications on this device" turned off.
  try {
    if (window.localStorage.getItem('vox:push:disabled') === '1') return;
  } catch {}
  if (!('serviceWorker' in navigator)) return;
  if (!('PushManager' in window)) return;
  if (!('Notification' in window)) return;

  const standalone = isStandaloneMode();
  const hostname = String(window.location.hostname || '').trim().toLowerCase();
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  const isSecure = !!window.isSecureContext || isLocal;
  if (!isSecure) return;

  let reg = null;
  try {
    reg = await navigator.serviceWorker.getRegistration('/');
  } catch {
    reg = null;
  }

  if (!reg) {
    reg = await navigator.serviceWorker.register('/vox-sw.js', { scope: '/' }).catch(() => null);
  }

  if (!reg) return;

  const keyRes = await fetch('/api/coms/push/public-key', {
    credentials: 'include',
    cache: 'no-store',
  }).catch(() => null);

  if (!keyRes || !keyRes.ok) return;

  const keyData = await keyRes.json().catch(() => null);
  const publicKey = keyData && keyData.publicKey ? String(keyData.publicKey).trim() : '';
  if (!publicKey) return;

  let permission = String(Notification.permission || '').trim();
  const alreadyPrompted = hasPromptedForPushOnce();

  if (permission === 'default' && standalone && !alreadyPrompted) {
    markPromptedForPushOnce();
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = String(Notification.permission || '').trim();
    }
  }

  if (permission !== 'granted') return;

  let sub = await reg.pushManager.getSubscription().catch(() => null);

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }).catch(() => null);
  }

  if (!sub) return;

  await fetch('/api/coms/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  }).catch(() => {});
}

export default function ComsApp({ user, setUser, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [joinBusy, setJoinBusy] = useState(false);
  const lastTriedRef = useRef('');
  const lastPushSyncRef = useRef('');
  const updateCheckRanRef = useRef(false);

  const isSignedIn = !!(user && user.id != null);

  // Paint the last theme used on this device before the first render (no dark flash for light themes).
  useState(() => {
    paintCachedTheme();
    return true;
  });
  // One account per page: switching or signing out anywhere in this browser restarts the app.
  useAccountSessionGuard(user);

  useEffect(() => {
    const inviteFromUrl = getInviteFromSearch(location && location.search ? location.search : '');
    const pending = readPendingInvite();
    const invite = inviteFromUrl || pending;

    if (!invite) return;

    if (!isSignedIn) {
      writePendingInvite(invite);
      navigate(`/?invite=${encodeURIComponent(invite)}`, { replace: true });
      return;
    }

    if (joinBusy) return;

    if (lastTriedRef.current === invite) return;
    lastTriedRef.current = invite;

    (async () => {
      setJoinBusy(true);
      try {
        const res = await fetch('/api/coms/join-by-invite', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invite_code: invite }),
        });

        const data = await res.json().catch(() => null);

        if (res.status === 401) {
          writePendingInvite(invite);
          navigate(`/?invite=${encodeURIComponent(invite)}`, { replace: true });
          return;
        }

        if (!res.ok || !data || data.error) {
          clearPendingInvite();
          navigate('/comms', { replace: true });
          const msg = (data && data.error) || 'Failed to join server.';
          try {
            window.alert(msg);
          } catch {}
          return;
        }

        clearPendingInvite();

        const sid = pickServerIdFromPayload(data);
        if (sid) {
          navigate(`/comms/${encodeURIComponent(sid)}`, { replace: true });
          return;
        }

        navigate('/comms', { replace: true });
      } catch {
        clearPendingInvite();
        navigate('/comms', { replace: true });
        try {
          window.alert('Failed to join server.');
        } catch {}
      } finally {
        setJoinBusy(false);
      }
    })();
  }, [location, isSignedIn, joinBusy, navigate]);

  useEffect(() => {
    if (!isTauriDesktop()) return;
    if (updateCheckRanRef.current) return;
    updateCheckRanRef.current = true;

    let cancelled = false;

    (async () => {
      let update = null;

      try {
        update = await checkForDesktopUpdate();
        if (cancelled || !update) return;

        const nextVersion = String(update.version || '').trim();
        const currentVersion = String(update.currentVersion || '').trim();

        if (!nextVersion || nextVersion === currentVersion) {
          try {
            await update.close?.();
          } catch {}
          return;
        }

        if (readSkippedUpdateVersion() === nextVersion) {
          try {
            await update.close?.();
          } catch {}
          return;
        }

        const body = String(update.body || '').trim();
        const lines = [
          `A new desktop app version is available.`,
          '',
          `Current version: ${currentVersion || 'unknown'}`,
          `New version: ${nextVersion}`,
        ];

        if (body) {
          lines.push('', 'Update notes:', body);
        }

        lines.push('', 'Install it now?');

        const shouldInstall = window.confirm(lines.join('\n'));
        if (!shouldInstall) {
          writeSkippedUpdateVersion(nextVersion);
          try {
            await update.close?.();
          } catch {}
          return;
        }

        clearSkippedUpdateVersion();
        await update.downloadAndInstall();
        if (cancelled) return;

        try {
          await update.close?.();
        } catch {}

        window.alert('Update installed. Voxemera will now restart.');
        await relaunchDesktopApp();
      } catch (err) {
        try {
          if (update && typeof update.close === 'function') {
            await update.close();
          }
        } catch {}
        console.error('Desktop update check failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isSignedIn) return;

    const userId = String(user && user.id != null ? user.id : '').trim();
    if (!userId) return;

    let alive = true;

    async function syncPush() {
      try {
        await ensureVoxPushSubscription();
      } catch {}
    }

    const syncKey = `${userId}:${isStandaloneMode() ? 'standalone' : 'browser'}`;
    if (lastPushSyncRef.current !== syncKey) {
      lastPushSyncRef.current = syncKey;
      syncPush().catch(() => {});
    }

    function onVisibilityChange() {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      syncPush().catch(() => {});
    }

    function onFocus() {
      if (!alive) return;
      syncPush().catch(() => {});
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
      // Settings asks for a sync right after the user allows notifications.
      window.addEventListener('vox-push-sync', onFocus);
    }

    return () => {
      alive = false;
      if (typeof window !== 'undefined') window.removeEventListener('vox-push-sync', onFocus);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [isSignedIn, user]);

  return (
    <PersonalisationProvider user={isSignedIn ? user : null}>
      <Routes>
        <Route index element={<CommsHomeView user={user} setUser={setUser} onLogout={onLogout} />} />
        <Route path=":serverId/*" element={<ComsLayout user={user} setUser={setUser} onLogout={onLogout} />} />
        <Route path="*" element={<Navigate to="/comms" replace />} />
      </Routes>
      <EncryptionConsentHost />
      <DeviceVerifyHost />
    </PersonalisationProvider>
  );
}